import mobx, { observable, runInAction, computed, action } from 'mobx';
import { createViewModel } from 'mobx-utils';
import WorkflowStepStateResource from './workflow-step-state-resource';
import _ from 'lodash';
import WorkflowStepStateModel from './workflow-step-state-model';
import config from 'react-global-configuration';
import Cons from './../../isomorphic/cons';
import WorkflowResource from './workflow-resource';
import TimelineWorkflowStore from './steps/timeline/timeline-workflow-step-store';

export default class WorkflowStore {
    @observable isLoading = false;
    @observable isStateLoaded = false;
    @observable styles = [];
    workflowState = observable.map();

    @computed
    get workflow() {
        if (!this.appStore.tcFileStore.selectedFile) return [];

        const steps = this.getWorkflowSteps().map(step => {
            const state = this.workflowState.get(step.stepId) || this.generateDefaultStepState(step);

            return _.assign(_.clone(step), { state: _.clone(state) });
        });

        return steps;
    }

    constructor(appStore) {
        this.appStore = appStore;
        this.workflowStepStateResource = new WorkflowStepStateResource();
        this.workflowResource = new WorkflowResource();
        this.timelineWorkflowStore = new TimelineWorkflowStore(this.appStore);
    }

    @action
    setLoading(loading) {
        this.isLoading = loading;
    }

    getWorkflowSteps() {
        const isWorkflowAll = this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.WORKFLOW_ALL);
        let steps = [];
        switch (this.appStore.tcFileStore.selectedFile.typeOfSale) {
            case Cons.saleTypes['Listing']:
                steps = steps.concat(
                    this.workflowResource.loadListingWorkflow(
                        isWorkflowAll,
                        this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.LISTING_WORKFLOW)
                    )
                );
                break;
            case Cons.saleTypes['Double Ended']:
                if (this.appStore.tcFileStore.selectedFile.originatedFromListing) {
                    steps = steps.concat(
                        this.workflowResource.loadListingToDoubleEndedWorkflow(
                            isWorkflowAll,
                            this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.DOUBLE_ENDED_WORKFLOW)
                        )
                    );
                } else if (this.appStore.tcFileStore.selectedFile.transactionId) {
                    steps = steps.concat(
                        this.workflowResource.loadDoubleEndedWorkflow(
                            isWorkflowAll,
                            this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.DOUBLE_ENDED_WORKFLOW)
                        )
                    );
                } else {
                    steps = steps.concat(
                        this.workflowResource.loadPreConvertedDoubleEndedWorkflow(
                            isWorkflowAll,
                            this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.DOUBLE_ENDED_WORKFLOW)
                        )
                    );
                }
                break;
            case Cons.saleTypes['Listing Gone Pending']:
                if (this.appStore.tcFileStore.selectedFile.originatedFromListing) {
                    steps = steps.concat(
                        this.workflowResource.loadListingToListingGonePendingWorkflow(
                            isWorkflowAll,
                            this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.LISTING_GONE_PENDING_WORKFLOW)
                        )
                    );
                } else if (this.appStore.tcFileStore.selectedFile.transactionId) {
                    steps = steps.concat(
                        this.workflowResource.loadListingGonePendingWorkflow(
                            isWorkflowAll,
                            this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.LISTING_GONE_PENDING_WORKFLOW)
                        )
                    );
                } else {
                    steps = steps.concat(
                        this.workflowResource.loadPreConvertedListingGonePendingWorkflow(
                            isWorkflowAll,
                            this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.LISTING_GONE_PENDING_WORKFLOW)
                        )
                    );
                }
                break;
            case Cons.saleTypes['Sale']:
                steps = steps.concat(
                    this.workflowResource.loadSalesWorkflow(
                        isWorkflowAll,
                        this.appStore.featureFlagStore.isFeatureEnabled(Cons.FEATURES.SALES_WORKFLOW)
                    )
                );
                break;
            default:
                steps = steps.concat([]);
        }

        if (this.calendarTimelineStepState && this.calendarTimelineStepState.data.timeline) {
            const includedAuditSteps = this.calendarTimelineStepState.data.timeline
                .filter(event => !event.exclude)
                .filter(event => !event.excludeFromWorkflow)
                .map(event => {
                    return {
                        type: event.eventType,
                        stepId: event.timelineEventId,
                        title: event.name,
                    };
                });

            steps = steps.concat(includedAuditSteps);
            steps = steps.concat(this.constantAuditWorkflowSteps);
        }

        return steps;
    }

    generateDefaultStepState(step) {
        const defaultStepStateModel = new WorkflowStepStateModel('new');
        defaultStepStateModel.tcFileId = this.appStore.tcFileStore.selectedFile.tcFileId;
        defaultStepStateModel.stepId = step.stepId;

        return defaultStepStateModel;
    }

    loadTimelineEvents() {
        this.timelineWorkflowStore.loadTimelineEvents();
    }

    loadAllAuditSteps(){
        return this.constantAuditWorkflowStepsAll;
    }
    loadWorkflow() {
        this.isLoading = true;
        this.isStateLoaded = false;
        return this.workflowStepStateResource
            .getStateForFile(this.appStore.authStore.getAccessToken(), this.appStore.tcFileStore.selectedFile)
            .then(steps => {
                runInAction(() => {
                    this.isLoading = false;
                    this.isStateLoaded = true;
                    this.workflowState.clear();
                    steps.forEach(step => {
                        this.workflowState.set(step.stepId, step);
                    });
                });
            })
            .catch(error => {
                this.isLoading = false;
                console.log(error);
            });
    }

    updateWorkflowStep(step) {
        this.isLoading = true;

        const dirtyProperties = [];
        if (!step.state.isNew) {
            const stepStateViewModel = createViewModel(this.workflowState.get(step.stepId));
            _.assign(stepStateViewModel, mobx.toJS(step.state));

            _.each(step.state, (value, key) => {
                if (stepStateViewModel.isPropertyDirty(key)) {
                    dirtyProperties.push(key);
                }
            });
        }

        return this.workflowStepStateResource
            .saveStepState(this.appStore.authStore.getAccessToken(), step.state, dirtyProperties)
            .then(updatedStepState => {
                this.isLoading = false;
                this.workflowState.set(updatedStepState.stepId, updatedStepState);
            })
            .catch(err => {
                this.isLoading = false;

                return err;
            });
    }

    resetWorkflowState() {
        this.isStateLoaded = false;
        this.workflowState.clear();
    }

    @computed
    get invoiceStepState() {
        return this.workflowState.get('21b9897a-132f-4cd5-bc1f-31f12e0be5ef');
    }

    @computed
    get calendarTimelineStepState() {
        return this.workflowState.get('572ace14-fb4a-4fdb-8acc-c9f4c3260c45');
    }

    get constantAuditWorkflowSteps() {
        return this.workflowResource.loadConstantAuditWorkflowSteps();
    }

    get constantAuditWorkflowStepsAll() {
        return this.workflowResource.loadAllSteps();
    }
}



// WEBPACK FOOTER //
// ./src/workflow/workflow-store.js